// Which value control a condition row takes, and which operators it offers,
// given the data key's type and options from the field catalog.
//
// The catalog publishes a key's `options` only when every data field on it
// can be enumerated, aggregated across the account. A partial list is a picker
// that cannot express a valid answer, so `options` is absent rather than short
// (see `OfferLetterTemplates::DataKeyOptions`).

import {
  CONDITION_OPERATORS,
  LIST_OPERATORS,
  VALUELESS_OPERATORS,
  isDisplayableDate,
  listEntries,
} from './constants';
import {
  CatalogField,
  CatalogFieldOption,
  CatalogGroup,
  ConditionOperator,
  FieldCondition,
} from './types';

export type ConditionValueControl =
  /** `is present` / `is blank`: the operator carries the whole condition. */
  | 'none'
  /** The key's options listed, and any typed value accepted. */
  | 'combobox'
  /** Several entries: the key's options listed, and typed entries accepted. */
  | 'multi'
  /** A calendar date. */
  | 'date'
  /** A boolean key with no published options. */
  | 'yesNo'
  | 'text';

/**
 * The stored answers of a boolean key, which the Yes/No pair writes. A
 * boolean the catalog can enumerate arrives with `options` instead, and those
 * carry the values its data fields actually store.
 */
export const BOOLEAN_VALUES = { yes: 'true', no: 'false' } as const;

const ORDERING_OPERATORS: ConditionOperator[] = ['greater_than', 'less_than'];

const hasOptions = (field: CatalogField | undefined): boolean =>
  (field?.options?.length ?? 0) > 0;

const isListOperator = (operator: ConditionOperator | undefined): boolean =>
  operator != null && LIST_OPERATORS.includes(operator);

/**
 * The catalog entry for a condition's data key, or undefined when the key is
 * unknown. `CatalogFieldSelect` already flags an unknown key; the value
 * control only degrades to free text.
 */
export const catalogFieldFor = (
  groups: CatalogGroup[],
  dataKey: string | undefined,
): CatalogField | undefined => {
  if (!dataKey) return undefined;

  return groups
    .flatMap(group => group.fields)
    .find(field => field.key === dataKey);
};

/**
 * Whether `greater than` / `less than` apply to this key. A boolean has no
 * order, and on a key whose whole value set is known an ordering comparison is
 * the near-miss the typed controls exist to stop.
 */
const allowsOrdering = (field: CatalogField | undefined): boolean =>
  field?.data_type !== 'boolean' && !hasOptions(field);

/**
 * The operators the row offers. The row's current operator stays in the list
 * even where it is withheld, so a saved condition never renders a blank
 * operator select over a value it still holds.
 */
export const operatorsFor = (
  field: CatalogField | undefined,
  current: ConditionOperator | undefined,
): ConditionOperator[] =>
  CONDITION_OPERATORS.filter(
    op =>
      op === current ||
      !ORDERING_OPERATORS.includes(op) ||
      allowsOrdering(field),
  );

/**
 * The control for one condition row.
 *
 * | Operator | Control |
 * | -- | -- |
 * | equals · not_equals | combobox over the key's options, else typed |
 * | in · not_in | multi-entry, listing the key's options when it has any |
 * | contains | free text |
 * | greater_than · less_than | date picker on a date key, else free text |
 * | present · blank | none |
 *
 * Typed, where the key has no options: date picker on a date key, Yes/No on a
 * boolean key, free text otherwise.
 */
export const conditionValueControl = (
  operator: ConditionOperator | undefined,
  field: CatalogField | undefined,
): ConditionValueControl => {
  if (operator != null && VALUELESS_OPERATORS.includes(operator)) return 'none';
  if (operator === 'contains') return 'text';
  if (isListOperator(operator)) return 'multi';

  if (operator != null && ORDERING_OPERATORS.includes(operator)) {
    return field?.data_type === 'date' ? 'date' : 'text';
  }

  // equals / not_equals, and the `undefined` operator the row renders as equals.
  if (hasOptions(field)) return 'combobox';
  if (field?.data_type === 'date') return 'date';
  if (field?.data_type === 'boolean') return 'yesNo';

  return 'text';
};

/**
 * Whether a control can show the value the row already holds. A date input
 * given a day that does not exist, or a Yes/No pair given any other answer,
 * renders empty over a condition that still holds and saves the original. The
 * caller falls back to text, which shows it.
 */
export const controlCanDisplay = (
  control: ConditionValueControl,
  condition: FieldCondition,
): boolean => {
  const raw = condition.value ?? '';

  if (control === 'date') return raw === '' || isDisplayableDate(raw);
  if (control === 'yesNo') {
    return raw === '' || Object.values(BOOLEAN_VALUES).includes(raw as never);
  }

  return true;
};

/**
 * The option a typed value names, matched on its stored value or its label
 * without regard to case. Typing "exempt" against an option stored as
 * "Exempt" writes the stored value, so the condition matches the applicant's
 * answer rather than missing it by a capital letter.
 */
export const optionForTyped = (
  options: CatalogFieldOption[],
  typed: string,
): CatalogFieldOption | undefined => {
  const needle = typed.trim().toLowerCase();
  if (needle === '') return undefined;

  return options.find(
    option =>
      option.value.toLowerCase() === needle ||
      option.label.toLowerCase() === needle,
  );
};

/** The value to store for what the author typed or picked. */
export const canonicalValue = (
  options: CatalogFieldOption[],
  typed: string,
): string => optionForTyped(options, typed)?.value ?? typed;

/**
 * The changes an operator switch makes. A list operator holds entries in
 * `values`; every other operator holds one string in `value`. A single value
 * carries across the switch in either direction, and several entries do not
 * collapse into one scalar that would compare as a literal.
 */
export const changesForOperator = (
  condition: FieldCondition,
  operator: ConditionOperator,
): Partial<FieldCondition> => {
  const wasList = isListOperator(condition.operator);
  const isList = isListOperator(operator);

  if (isList && !wasList) {
    const scalar = (condition.value ?? '').trim();
    return { operator, value: '', values: scalar === '' ? [] : [scalar] };
  }

  if (wasList && !isList) {
    const entries = listEntries(condition);
    return {
      operator,
      value: entries.length === 1 ? entries[0] : '',
      values: undefined,
    };
  }

  return { operator };
};

/**
 * The changes a data key switch makes. A value written for one key is not a
 * value of another's type, so it clears, and an operator the new key withholds
 * falls back to `equals`.
 */
export const changesForDataKey = (
  condition: FieldCondition,
  dataKey: string,
  field: CatalogField | undefined,
): Partial<FieldCondition> => {
  const changes: Partial<FieldCondition> = { data_key: dataKey };
  if (dataKey === condition.data_key) return changes;

  if (
    condition.operator != null &&
    !operatorsFor(field, undefined).includes(condition.operator)
  ) {
    changes.operator = 'equals';
  }

  if ((condition.value ?? '') !== '' || (condition.values?.length ?? 0) > 0) {
    changes.value = '';
    if (isListOperator(changes.operator ?? condition.operator)) {
      changes.values = [];
    } else if (condition.values !== undefined) {
      changes.values = undefined;
    }
  }

  return changes;
};
